import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, LogOut, CircleCheck as CheckCircle, TriangleAlert as AlertTriangle, Users, ChevronLeft, ChevronRight, RefreshCw, BadgeCheck, Ban, UtensilsCrossed, ShoppingBag, Package, Clock, UserX, Trash2, X, ClipboardList, ShieldCheck, Megaphone, Send, ImagePlus, MapPin, Star, Truck, FileCheck, User, FileText, Award, ExternalLink } from 'lucide-react';
import { ADMIN_DASHBOARD_URL, ADMIN_USERS_URL, ADMIN_BLOCK_USER_URL, ADMIN_UNBLOCK_USER_URL, ADMIN_KITCHENS_URL, ADMIN_REVIEW_KITCHEN_URL, ADMIN_BLOCK_KITCHEN_URL, ADMIN_UNBLOCK_KITCHEN_URL, ADMIN_PENDING_KITCHENS_URL, ADMIN_FOODS_URL, ADMIN_REMOVE_FOOD_URL, ADMIN_AUDIT_LOGS_URL, ADMIN_SEND_PROMOTION_URL, ADMIN_DEBUG_PUSH_URL } from '../../config/api';
import { getFirstImageUrl } from '../Utils/apiHelpers';
import { normalizeKitchensResponse, NormalizedKitchen } from '../Utils/normalizeKitchen';
import { usePaginatedFetch } from '../Utils/usePaginatedFetch';
import '../Admin/AdminDashboard.css';

interface User {
  user_id: number;
  name: string | null;
  email: string;
  role: string[];
  country: string | null;
  city: string | null;
  state: string | null;
  is_verified: boolean;
  is_blocked: boolean;
  last_active: string | null;
  last_login: string | null;
  phone_number: string | null;
  phone: string | null;
  profile_photo: string | null;
  profile_completed: boolean;
  onboarding_step: number;
  gender: string | null;
  dob: string | null;
  bio: string | null;
  pincode: string | null;
  address: string | null;
  latitude: number | null;
  longitude: number | null;
  food_preferences: string | null;
  dietary_preference: string | null;
  favorite_cuisine: string | null;
  current_latitude: number | null;
  current_longitude: number | null;
  current_address: string | null;
  current_city: string | null;
  device_token: string | null;
}

interface UsersResponse {
  success: boolean;
  count: number;
  users: User[];
}

type Kitchen = NormalizedKitchen;

interface PendingKitchen {
  kitchen_id: number;
  kitchen_name: string;
  kitchen_type: string;
  city: string;
  state: string;
  owner: {
    name: string;
    email: string;
    phone: string;
  };
  documents: {
    kitchen_photo: string;
    photo_id_url: string | null;
    photo_id_type: string | null;
    training_certificate_url: string | null;
    verification_status: string;
  };
  created_at: string;
  action_url: string;
}

interface PendingKitchensResponse {
  success: boolean;
  total: number;
  kitchens: PendingKitchen[];
}

interface Food {
  food_id: number;
  food_name: string;
  description: string;
  category: string;
  price: number;
  serving_size: string;
  available_quantity: number;
  is_available: boolean;
  kitchen_id: number;
  user_id: number | null;
  phone_number: string;
  pickup_location: string;
  preparation_time: string;
  image_urls: string;
  low_stock_threshold: number;
  is_deleted?: boolean;
  total_orders?: number;
  average_rating?: number;
  total_reviews?: number;
  is_featured?: boolean;
  is_bestseller?: boolean;
  is_recommended?: boolean;
  veg_type?: string | null;
  discount_percentage?: number;
  preparation_time_minutes?: number;
  calories?: number | null;
  created_at?: string;
  updated_at?: string;
}

interface FoodsResponse {
  success: boolean;
  count: number;
  foods: Food[];
}

interface AuditLog {
  _id: string;
  admin_id: number;
  action: string;
  target_id: number;
  target_type: string;
  details: string;
  created_at: string;
}

interface AuditLogsResponse {
  success: boolean;
  count: number;
  logs: AuditLog[];
}

interface DashboardStats {
  total_users: number;
  total_kitchens: number;
  total_foods: number;
  total_orders: number;
  pending_kitchens: number;
  blocked_users: number;
}

const USERS_PER_PAGE = 20;
const KITCHENS_PER_PAGE = 10;
const PENDING_PER_PAGE = 5;
const FOODS_PER_PAGE = 20;
const AUDIT_LOGS_PER_PAGE = 20;
const STATS_REFRESH_INTERVAL = 30000;

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();

  // Read from localStorage once on mount — stored as React state so effects re-run on change
  const [adminToken] = useState<string>(() => localStorage.getItem('adminToken') || '');
  const adminEmail = localStorage.getItem('adminEmail') || '';

  // Dashboard stats state
  const [dashboardStats, setDashboardStats] = useState<DashboardStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Users state
  const users = usePaginatedFetch<User, UsersResponse>({
    fetchUrl: (page, limit) => ADMIN_USERS_URL(page, limit),
    perPage: USERS_PER_PAGE,
    token: adminToken,
    extractData: (res) => res.users,
    inferHasNext: true,
  });

  // Kitchens state
  const [kitchensTotal, setKitchensTotal] = useState(0);
  const kitchens = usePaginatedFetch<Kitchen, any>({
    fetchUrl: (page, limit) => ADMIN_KITCHENS_URL(page, limit),
    perPage: KITCHENS_PER_PAGE,
    token: adminToken,
    extractData: (raw) => {
      const data = normalizeKitchensResponse(raw);
      return data.kitchens;
    },
    totalBased: {
      extractTotal: (raw) => {
        const data = normalizeKitchensResponse(raw);
        return data.total;
      },
      extractHasNext: (raw) => {
        const data = normalizeKitchensResponse(raw);
        return data.has_next;
      },
      extractHasPrev: (raw) => {
        const data = normalizeKitchensResponse(raw);
        return data.has_prev;
      },
    },
    onResponse: (raw) => {
      const data = normalizeKitchensResponse(raw);
      setKitchensTotal(data.total);
    },
  });

  // Pending kitchens state
  const [pendingKitchens, setPendingKitchens] = useState<PendingKitchen[]>([]);
  const [pendingKitchensLoading, setPendingKitchensLoading] = useState(false);
  const [pendingKitchensError, setPendingKitchensError] = useState('');
  const [pendingPage, setPendingPage] = useState(1);

  // Kitchen approve/reject state
  const [kitchenToAction, setKitchenToAction] = useState<(Kitchen | PendingKitchen) | null>(null);
  const [showKitchenActionModal, setShowKitchenActionModal] = useState(false);
  const [kitchenAction, setKitchenAction] = useState<'approve' | 'reject'>('approve');
  const [rejectReason, setRejectReason] = useState('');
  const [kitchenActioning, setKitchenActioning] = useState(false);
  const [kitchenActionError, setKitchenActionError] = useState('');

  // Kitchen block/unblock state
  const [kitchenToBlock, setKitchenToBlock] = useState<Kitchen | null>(null);
  const [showBlockKitchenModal, setShowBlockKitchenModal] = useState(false);
  const [blockKitchenAction, setBlockKitchenAction] = useState<'block' | 'unblock'>('block');
  const [blockingKitchen, setBlockingKitchen] = useState(false);
  const [blockKitchenError, setBlockKitchenError] = useState('');

  // Foods state
  const foods = usePaginatedFetch<Food, FoodsResponse>({
    fetchUrl: (page, limit) => ADMIN_FOODS_URL(page, limit),
    perPage: FOODS_PER_PAGE,
    token: adminToken,
    extractData: (res) => res.foods,
    inferHasNext: true,
  });

  // Remove food state
  const [foodToRemove, setFoodToRemove] = useState<Food | null>(null);
  const [showRemoveFoodModal, setShowRemoveFoodModal] = useState(false);
  const [removeFoodReason, setRemoveFoodReason] = useState('');
  const [removingFood, setRemovingFood] = useState(false);
  const [removeFoodError, setRemoveFoodError] = useState('');

  // Block/unblock user state
  const [userToBlock, setUserToBlock] = useState<User | null>(null);
  const [showBlockUserModal, setShowBlockUserModal] = useState(false);
  const [blockAction, setBlockAction] = useState<'block' | 'unblock'>('block');
  const [blockingUser, setBlockingUser] = useState(false);
  const [blockUserError, setBlockUserError] = useState('');

  // Audit logs state
  const [auditLogsCount, setAuditLogsCount] = useState(0);
  const auditLogs = usePaginatedFetch<AuditLog, AuditLogsResponse>({
    fetchUrl: (page, limit) => ADMIN_AUDIT_LOGS_URL(page, limit),
    perPage: AUDIT_LOGS_PER_PAGE,
    token: adminToken,
    extractData: (res) => res.logs,
    inferHasNext: true,
    onResponse: (res) => setAuditLogsCount(res.count),
  });

  // Tab navigation state
  type TabId = 'users' | 'pending' | 'kitchens' | 'foods' | 'audit' | 'promotions';
  const [activeTab, setActiveTab] = useState<TabId>('users');

  // Promotions state
  const [promoTitle, setPromoTitle] = useState('');
  const [promoMessage, setPromoMessage] = useState('');
  const [promoBanner, setPromoBanner] = useState<File | null>(null);
  const [promoBannerPreview, setPromoBannerPreview] = useState('');
  const [promoCity, setPromoCity] = useState('');
  const [promoState, setPromoState] = useState('');
  const [promoCountry, setPromoCountry] = useState('');
  const [promoRole, setPromoRole] = useState('');
  const [promoDietaryPreference, setPromoDietaryPreference] = useState('');
  const [promoSendToAll, setPromoSendToAll] = useState(false);
  const [promoInactiveDays, setPromoInactiveDays] = useState('');
  const [promoFavoriteCategory, setPromoFavoriteCategory] = useState('');
  const [promoScheduleAt, setPromoScheduleAt] = useState('');
  const [promoSending, setPromoSending] = useState(false);
  const [promoSuccess, setPromoSuccess] = useState('');
  const [promoError, setPromoError] = useState('');
  const [debugPushLoading, setDebugPushLoading] = useState(false);
  const [debugPushResult, setDebugPushResult] = useState<any>(null);
  const [debugPushError, setDebugPushError] = useState('');
  const promoBannerInputRef = useRef<HTMLInputElement>(null);

  // Kitchen detail modal state
  const [selectedKitchen, setSelectedKitchen] = useState<Kitchen | null>(null);
  const [showKitchenDetailModal, setShowKitchenDetailModal] = useState(false);

  // Redirect to admin login if no token or stale corrupted token (full JSON object stored as string)
  useEffect(() => {
    if (!adminToken || adminToken.trim().startsWith('{')) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminEmail');
      localStorage.removeItem('adminRole');
      navigate('/admin', { replace: true });
    }
  }, [adminToken, navigate]);

  const fetchDashboardStats = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;

    setStatsLoading(true);
    try {
      const response = await fetch(
        ADMIN_DASHBOARD_URL,
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      if (response.ok) {
        const data: DashboardStats = await response.json();
        setDashboardStats(data);
      }
    } catch {
      // Silently fail — stats are informational
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!adminToken) return;
    fetchDashboardStats();
    const interval = setInterval(fetchDashboardStats, STATS_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [adminToken, fetchDashboardStats]);

  const fetchPendingKitchens = useCallback(async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;

    setPendingKitchensLoading(true);
    setPendingKitchensError('');

    try {
      const response = await fetch(
        ADMIN_PENDING_KITCHENS_URL(1, 100),
        {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data: PendingKitchensResponse = await response.json();
        setPendingKitchens(data.kitchens || []);
      } else if (response.status === 401 || response.status === 403) {
        setPendingKitchensError('Session expired or access denied. Please log out and log in again.');
      } else {
        setPendingKitchensError(`Failed to load pending kitchens (${response.status}). Please try again.`);
      }
    } catch {
      setPendingKitchensError('Network error. Please check your connection and try again.');
    } finally {
      setPendingKitchensLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adminToken) {
      fetchPendingKitchens();
    }
  }, [adminToken, fetchPendingKitchens]);

  const openRemoveFoodModal = (food: Food) => {
    setFoodToRemove(food);
    setRemoveFoodReason('');
    setRemoveFoodError('');
    setShowRemoveFoodModal(true);
  };

  const closeRemoveFoodModal = () => {
    setShowRemoveFoodModal(false);
    setFoodToRemove(null);
    setRemoveFoodReason('');
    setRemoveFoodError('');
  };

  const handleRemoveFood = async () => {
    if (!foodToRemove || !removeFoodReason.trim()) return;

    const token = localStorage.getItem('adminToken');
    if (!token) return;

    setRemovingFood(true);
    setRemoveFoodError('');

    try {
      const response = await fetch(
        ADMIN_REMOVE_FOOD_URL(foodToRemove.food_id, removeFoodReason.trim()),
        {
          method: 'DELETE',
          headers: {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        closeRemoveFoodModal();
        foods.fetch(foods.page);
      } else if (response.status === 401 || response.status === 403) {
        setRemoveFoodError('Session expired or access denied.');
      } else {
        const data = await response.json().catch(() => ({}));
        const msg = typeof data.message === 'string' ? data.message : `Failed to remove food (${response.status}).`;
        setRemoveFoodError(msg);
      }
    } catch {
      setRemoveFoodError('Network error. Please try again.');
    } finally {
      setRemovingFood(false);
    }
  };

  const openBlockUserModal = (user: User, action: 'block' | 'unblock') => {
    setUserToBlock(user);
    setBlockAction(action);
    setBlockUserError('');
    setShowBlockUserModal(true);
  };

  const closeBlockUserModal = () => {
    setShowBlockUserModal(false);
    setUserToBlock(null);
    setBlockUserError('');
  };

  const handleBlockUser = async () => {
    if (!userToBlock) return;

    const token = localStorage.getItem('adminToken');
    if (!token) return;

    setBlockingUser(true);
    setBlockUserError('');

    const endpoint = blockAction === 'block'
      ? ADMIN_BLOCK_USER_URL(userToBlock.user_id)
      : ADMIN_UNBLOCK_USER_URL(userToBlock.user_id);

    try {
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        users.setData(prev =>
          prev.map(u =>
            u.user_id === userToBlock.user_id ? { ...u, is_blocked: blockAction === 'block' } : u
          )
        );
        closeBlockUserModal();
        fetchDashboardStats();
      } else if (response.status === 401 || response.status === 403) {
        setBlockUserError('Session expired or access denied.');
      } else {
        const data = await response.json().catch(() => ({}));
        const msg = typeof data.message === 'string'
          ? data.message
          : `Failed to ${blockAction} user (${response.status}).`;
        setBlockUserError(msg);
      }
    } catch {
      setBlockUserError('Network error. Please try again.');
    } finally {
      setBlockingUser(false);
    }
  };

  const openKitchenActionModal = (kitchen: Kitchen | PendingKitchen, action: 'approve' | 'reject') => {
    setKitchenToAction(kitchen);
    setKitchenAction(action);
    setRejectReason('');
    setKitchenActionError('');
    setShowKitchenActionModal(true);
  };

  const closeKitchenActionModal = () => {
    setShowKitchenActionModal(false);
    setKitchenToAction(null);
    setRejectReason('');
    setKitchenActionError('');
  };

  const handleKitchenAction = async () => {
    if (!kitchenToAction) return;
    if (kitchenAction === 'reject' && !rejectReason.trim()) return;

    const token = localStorage.getItem('adminToken');
    if (!token) return;

    setKitchenActioning(true);
    setKitchenActionError('');

    try {
      const response = await fetch(ADMIN_REVIEW_KITCHEN_URL(kitchenToAction.kitchen_id), {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          action: kitchenAction,
          rejection_reason: kitchenAction === 'reject' ? rejectReason.trim() : '',
        }),
      });

      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        setPendingKitchens(prev => prev.filter(k => k.kitchen_id !== kitchenToAction.kitchen_id));
        kitchens.setData(prev => prev.map(k =>
          k.kitchen_id === kitchenToAction.kitchen_id
            ? {
                ...k,
                is_approved: data.is_approved ?? (kitchenAction === 'approve'),
                status: data.status ?? (kitchenAction === 'approve'),
                approval_status: data.document_verification_status ?? (kitchenAction === 'approve' ? 'approved' : 'rejected'),
                document_verification_status: data.document_verification_status ?? k.document_verification_status,
                rejection_reason: data.rejection_reason ?? null,
              }
            : k
        ));
        closeKitchenActionModal();
        fetchDashboardStats();
        fetchPendingKitchens();
      } else if (response.status === 401 || response.status === 403) {
        setKitchenActionError('Session expired or access denied.');
      } else {
        const data = await response.json().catch(() => ({}));
        const msg = typeof data.message === 'string'
          ? data.message
          : `Failed to ${kitchenAction} kitchen (${response.status}).`;
        setKitchenActionError(msg);
      }
    } catch {
      setKitchenActionError('Network error. Please try again.');
    } finally {
      setKitchenActioning(false);
    }
  };

  const openBlockKitchenModal = (kitchen: Kitchen, action: 'block' | 'unblock') => {
    setKitchenToBlock(kitchen);
    setBlockKitchenAction(action);
    setBlockKitchenError('');
    setShowBlockKitchenModal(true);
  };

  const closeBlockKitchenModal = () => {
    setShowBlockKitchenModal(false);
    setKitchenToBlock(null);
    setBlockKitchenError('');
  };

  const handleBlockKitchen = async () => {
    if (!kitchenToBlock) return;

    const token = localStorage.getItem('adminToken');
    if (!token) return;

    setBlockingKitchen(true);
    setBlockKitchenError('');

    const endpoint = blockKitchenAction === 'block'
      ? ADMIN_BLOCK_KITCHEN_URL(kitchenToBlock.kitchen_id)
      : ADMIN_UNBLOCK_KITCHEN_URL(kitchenToBlock.kitchen_id);

    try {
      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const unblock = blockKitchenAction === 'unblock';
        kitchens.setData(prev => prev.map(k =>
          k.kitchen_id === kitchenToBlock.kitchen_id
            ? { ...k, is_blocked: !unblock, status: unblock }
            : k
        ));
        closeBlockKitchenModal();
        fetchDashboardStats();
      } else if (response.status === 401 || response.status === 403) {
        setBlockKitchenError('Session expired or access denied.');
      } else {
        const data = await response.json().catch(() => ({}));
        const msg = typeof data.message === 'string'
          ? data.message
          : `Failed to ${blockKitchenAction} kitchen (${response.status}).`;
        setBlockKitchenError(msg);
      }
    } catch {
      setBlockKitchenError('Network error. Please try again.');
    } finally {
      setBlockingKitchen(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminEmail');
    localStorage.removeItem('adminRole');
    localStorage.removeItem('adminPermissions');

    navigate('/admin');
  };

  const handleDebugPush = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) return;

    setDebugPushLoading(true);
    setDebugPushError('');
    setDebugPushResult(null);

    try {
      const response = await fetch(ADMIN_DEBUG_PUSH_URL, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setDebugPushResult(data);
      } else if (response.status === 401 || response.status === 403) {
        setDebugPushError('Session expired or access denied.');
      } else {
        const data = await response.json().catch(() => ({}));
        setDebugPushError(typeof data.message === 'string' ? data.message : `Failed (${response.status}).`);
      }
    } catch {
      setDebugPushError('Network error. Please try again.');
    } finally {
      setDebugPushLoading(false);
    }
  };

  const formatLastActive = (dateString: string | null): string => {
    if (!dateString) return '—';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const pendingTotalPages = Math.max(1, Math.ceil(pendingKitchens.length / PENDING_PER_PAGE));
  const paginatedPending = pendingKitchens.slice(
    (pendingPage - 1) * PENDING_PER_PAGE,
    pendingPage * PENDING_PER_PAGE
  );

  const handlePendingPageChange = (newPage: number) => {
    setPendingPage(newPage);
    const section = document.querySelector('.homecookt-pending-section');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const getPendingPageNumbers = (): number[] => {
    const delta = 2;
    const range: number[] = [];
    const left = Math.max(1, pendingPage - delta);
    const right = Math.min(pendingTotalPages, pendingPage + delta);
    for (let i = left; i <= right; i++) {
      range.push(i);
    }
    return range;
  };

  const isPlaceholder = (val: string | null | undefined): boolean =>
    !val || val.toLowerCase() === 'string' || val.trim() === '';

  const sanitize = (val: string | null | undefined): string =>
    isPlaceholder(val) ? '—' : val!;

  const formatCreatedAt = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="homecookt-admin-dashboard-page">
      {/* Header */}
      <header className="homecookt-admin-header">
        <div className="homecookt-admin-header-content">
          <div className="homecookt-admin-logo">
            <Shield size={24} />
            <span>HomeCookt Admin</span>
          </div>

          <div className="homecookt-admin-user-info">
            <div className="homecookt-admin-user-details">
              <span className="homecookt-admin-user-email">{adminEmail}</span>
              <span className="homecookt-admin-user-role">Administrator</span>
            </div>
            <button onClick={handleLogout} className="homecookt-admin-logout-btn">
              <LogOut size={16} />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="homecookt-admin-main">
        <h1 className="homecookt-admin-title">Admin Dashboard</h1>
        <p className="homecookt-admin-subtitle">
          Manage subscriptions and monitor platform activity
        </p>

        {/* Dashboard Stats Overview */}
        <div className="homecookt-stats-grid">
          <button
            className="homecookt-stat-card"
            onClick={() => setActiveTab('users')}
          >
            <div className="homecookt-stat-icon homecookt-stat-icon--users">
              <Users size={22} />
            </div>
            <div className="homecookt-stat-info">
              <span className="homecookt-stat-value">
                {statsLoading && !dashboardStats ? '—' : dashboardStats?.total_users ?? 0}
              </span>
              <span className="homecookt-stat-label">Total Users</span>
            </div>
          </button>

          <button
            className="homecookt-stat-card"
            onClick={() => setActiveTab('kitchens')}
          >
            <div className="homecookt-stat-icon homecookt-stat-icon--kitchens">
              <UtensilsCrossed size={22} />
            </div>
            <div className="homecookt-stat-info">
              <span className="homecookt-stat-value">
                {statsLoading && !dashboardStats ? '—' : dashboardStats?.total_kitchens ?? 0}
              </span>
              <span className="homecookt-stat-label">Total Kitchens</span>
            </div>
          </button>

          <button
            className="homecookt-stat-card"
            onClick={() => setActiveTab('foods')}
          >
            <div className="homecookt-stat-icon homecookt-stat-icon--foods">
              <ShoppingBag size={22} />
            </div>
            <div className="homecookt-stat-info">
              <span className="homecookt-stat-value">
                {statsLoading && !dashboardStats ? '—' : dashboardStats?.total_foods ?? 0}
              </span>
              <span className="homecookt-stat-label">Total Foods</span>
            </div>
          </button>

          <button
            className="homecookt-stat-card"
            disabled
          >
            <div className="homecookt-stat-icon homecookt-stat-icon--orders">
              <Package size={22} />
            </div>
            <div className="homecookt-stat-info">
              <span className="homecookt-stat-value">
                {statsLoading && !dashboardStats ? '—' : dashboardStats?.total_orders ?? 0}
              </span>
              <span className="homecookt-stat-label">Total Orders</span>
            </div>
          </button>

          <button
            className={`homecookt-stat-card ${dashboardStats && dashboardStats.pending_kitchens > 0 ? 'homecookt-stat-card--warning' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            <div className="homecookt-stat-icon homecookt-stat-icon--pending">
              <Clock size={22} />
            </div>
            <div className="homecookt-stat-info">
              <span className="homecookt-stat-value">
                {statsLoading && !dashboardStats ? '—' : dashboardStats?.pending_kitchens ?? 0}
              </span>
              <span className="homecookt-stat-label">Pending Kitchens</span>
            </div>
          </button>

          <button
            className={`homecookt-stat-card ${dashboardStats && dashboardStats.blocked_users > 0 ? 'homecookt-stat-card--warning' : ''}`}
            disabled
          >
            <div className="homecookt-stat-icon homecookt-stat-icon--blocked">
              <UserX size={22} />
            </div>
            <div className="homecookt-stat-info">
              <span className="homecookt-stat-value">
                {statsLoading && !dashboardStats ? '—' : dashboardStats?.blocked_users ?? 0}
              </span>
              <span className="homecookt-stat-label">Blocked Users</span>
            </div>
          </button>
          <button
            className="homecookt-stat-card"
            onClick={() => setActiveTab('audit')}
          >
            <div className="homecookt-stat-icon homecookt-stat-icon--audit">
              <ClipboardList size={22} />
            </div>
            <div className="homecookt-stat-info">
              <span className="homecookt-stat-value">
                {auditLogsCount}
              </span>
              <span className="homecookt-stat-label">Audit Logs</span>
            </div>
          </button>
        </div>

        {/* Tab Navigation Bar */}
        <nav className="homecookt-tab-nav">
          <button
            className={`homecookt-tab-btn ${activeTab === 'users' ? 'homecookt-tab-btn--active' : ''}`}
            onClick={() => setActiveTab('users')}
          >
            <Users size={16} />
            <span>All Users</span>
          </button>
          <button
            className={`homecookt-tab-btn ${activeTab === 'pending' ? 'homecookt-tab-btn--active' : ''}`}
            onClick={() => setActiveTab('pending')}
          >
            <Clock size={16} />
            <span>Pending Approvals</span>
            {pendingKitchens.length > 0 && (
              <span className="homecookt-tab-badge">{pendingKitchens.length}</span>
            )}
          </button>
          <button
            className={`homecookt-tab-btn ${activeTab === 'kitchens' ? 'homecookt-tab-btn--active' : ''}`}
            onClick={() => setActiveTab('kitchens')}
          >
            <UtensilsCrossed size={16} />
            <span>All Kitchens</span>
          </button>
          <button
            className={`homecookt-tab-btn ${activeTab === 'foods' ? 'homecookt-tab-btn--active' : ''}`}
            onClick={() => setActiveTab('foods')}
          >
            <ShoppingBag size={16} />
            <span>All Foods</span>
          </button>
          <button
            className={`homecookt-tab-btn ${activeTab === 'audit' ? 'homecookt-tab-btn--active' : ''}`}
            onClick={() => setActiveTab('audit')}
          >
            <ClipboardList size={16} />
            <span>Audit Logs</span>
          </button>
          <button
            className={`homecookt-tab-btn ${activeTab === 'promotions' ? 'homecookt-tab-btn--active' : ''}`}
            onClick={() => setActiveTab('promotions')}
          >
            <Megaphone size={16} />
            <span>Send Promotion</span>
          </button>
        </nav>

        {/* Users Section */}
        {activeTab === 'users' && (
        <div className="homecookt-users-section">
          <div className="homecookt-users-header">
            <div className="homecookt-users-title-group">
              <div className="homecookt-users-title-icon">
                <Users size={22} />
              </div>
              <div>
                <h2 className="homecookt-users-title">All Users</h2>
                {users.data.length > 0 && !users.loading && (
                  <p className="homecookt-users-count">
                    Showing {users.data.length} users — page {users.page}{users.hasNextPage ? '+' : ''}
                  </p>
                )}
              </div>
            </div>
            <button
              className="homecookt-users-refresh-btn"
              onClick={() => { users.resetPagination(); users.setPage(1); }}
              disabled={users.loading}
              title="Refresh users"
            >
              <RefreshCw size={16} className={users.loading ? 'spinning' : ''} />
              Refresh
            </button>
          </div>

          {/* Loading State */}
          {users.loading && (
            <div className="homecookt-users-loading">
              <div className="homecookt-users-spinner"></div>
              <span>Loading users...</span>
            </div>
          )}

          {/* Error State */}
          {users.error && !users.loading && (
            <div className="homecookt-users-error">
              <AlertTriangle size={20} />
              <span>{users.error}</span>
              <button
                className="homecookt-users-retry-btn"
                onClick={() => users.fetch(users.page)}
              >
                Retry
              </button>
            </div>
          )}

          {/* Table */}
          {!users.loading && !users.error && users.data.length > 0 && (
            <>
              <div className="homecookt-users-table-wrapper">
                <table className="homecookt-users-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Country</th>
                      <th>City</th>
                      <th>State</th>
                      <th>Phone</th>
                      <th>Verified</th>
                      <th>Blocked</th>
                      <th>Last Active</th>
                      <th>Last Login</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.data.map((user) => (
                      <tr key={user.user_id}>
                        <td className="homecookt-users-td-photo">
                          {user.profile_photo && user.profile_photo !== 'string' ? (
                            <img src={user.profile_photo} alt={user.name || 'User'} className="homecookt-users-table-photo" />
                          ) : (
                            <div className="homecookt-users-table-photo-fallback">
                              <User size={16} />
                            </div>
                          )}
                        </td>
                        <td className="homecookt-users-td-id">#{user.user_id}</td>
                        <td className="homecookt-users-td-name">{user.name || '—'}</td>
                        <td className="homecookt-users-td-email">{user.email}</td>
                        <td>
                          <div className="homecookt-role-badges">
                            {(user.role || []).map((r) => (
                              <span key={r} className={`homecookt-role-badge homecookt-role-badge--${r}`}>
                                {r.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td>{user.country || '—'}</td>
                        <td>{user.city || '—'}</td>
                        <td>{user.state || '—'}</td>
                        <td>{user.phone_number || user.phone || '—'}</td>
                        <td>
                          {user.is_verified ? (
                            <span className="homecookt-status-icon homecookt-status-icon--yes" title="Verified">
                              <BadgeCheck size={18} />
                            </span>
                          ) : (
                            <span className="homecookt-status-icon homecookt-status-icon--no" title="Not verified">
                              <Ban size={18} />
                            </span>
                          )}
                        </td>
                        <td>
                          {user.is_blocked ? (
                            <span className="homecookt-status-icon homecookt-status-icon--blocked" title="Blocked">
                              <Ban size={18} />
                            </span>
                          ) : (
                            <span className="homecookt-status-icon homecookt-status-icon--active" title="Active">
                              <BadgeCheck size={18} />
                            </span>
                          )}
                        </td>
                        <td className="homecookt-users-td-date">{formatLastActive(user.last_active)}</td>
                        <td className="homecookt-users-td-date">{formatLastActive(user.last_login)}</td>
                        <td>
                          {!user.role.includes('admin') && (
                            user.is_blocked ? (
                              <button
                                className="homecookt-user-action-btn homecookt-user-action-btn--unblock"
                                onClick={() => openBlockUserModal(user, 'unblock')}
                                title="Unblock user"
                              >
                                <ShieldCheck size={14} />
                                Unblock
                              </button>
                            ) : (
                              <button
                                className="homecookt-user-action-btn homecookt-user-action-btn--block"
                                onClick={() => openBlockUserModal(user, 'block')}
                                title="Block user"
                              >
                                <Ban size={14} />
                                Block
                              </button>
                            )
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {(users.totalPages > 1 || users.hasNextPage) && (
                <div className="homecookt-pagination">
                  <button
                    className="homecookt-pagination-btn homecookt-pagination-btn--nav"
                    onClick={() => users.handlePageChange(users.page - 1, '.homecookt-users-section')}
                    disabled={users.page === 1}
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>

                  <div className="homecookt-pagination-pages">
                    {users.page > 3 && (
                      <>
                        <button
                          className="homecookt-pagination-btn"
                          onClick={() => users.handlePageChange(1, '.homecookt-users-section')}
                        >
                          1
                        </button>
                        {users.page > 4 && (
                          <span className="homecookt-pagination-ellipsis">…</span>
                        )}
                      </>
                    )}

                    {users.getPageNumbers().map((pageNum) => (
                      <button
                        key={pageNum}
                        className={`homecookt-pagination-btn ${pageNum === users.page ? 'active' : ''}`}
                        onClick={() => users.handlePageChange(pageNum, '.homecookt-users-section')}
                      >
                        {pageNum}
                      </button>
                    ))}

                    {users.page < users.totalPages - 2 && (
                      <>
                        {users.page < users.totalPages - 3 && (
                          <span className="homecookt-pagination-ellipsis">…</span>
                        )}
                        <button
                          className="homecookt-pagination-btn"
                          onClick={() => users.handlePageChange(users.totalPages, '.homecookt-users-section')}
                        >
                          {users.totalPages}
                        </button>
                      </>
                    )}
                  </div>

                  <div className="homecookt-pagination-info">
                    Page {users.page}{users.hasNextPage ? '' : ` of ${users.totalPages}`}
                  </div>

                  <button
                    className="homecookt-pagination-btn homecookt-pagination-btn--nav"
                    onClick={() => users.handlePageChange(users.page + 1, '.homecookt-users-section')}
                    disabled={!users.hasNextPage}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}

          {/* Empty state */}
          {!users.loading && !users.error && users.data.length === 0 && (
            <div className="homecookt-users-empty">
              <Users size={40} />
              <p>No users found.</p>
            </div>
          )}
        </div>
        )}

        {/* Pending Kitchens Section */}
        {activeTab === 'pending' && (
        <div className="homecookt-pending-section">
          <div className="homecookt-users-header">
            <div className="homecookt-users-title-group">
              <div className="homecookt-pending-title-icon">
                <UtensilsCrossed size={22} />
              </div>
              <div>
                <div className="homecookt-pending-title-row">
                  <h2 className="homecookt-users-title">Pending Approvals</h2>
                  {pendingKitchens.length > 0 && !pendingKitchensLoading && (
                    <span className="homecookt-pending-count-badge">{pendingKitchens.length}</span>
                  )}
                </div>
                <p className="homecookt-users-count">
                  {pendingKitchens.length > 0
                    ? `${pendingKitchens.length} awaiting review — page ${pendingPage} of ${pendingTotalPages}`
                    : 'Kitchens awaiting review'}
                </p>
              </div>
            </div>
            <button
              className="homecookt-users-refresh-btn"
              onClick={() => fetchPendingKitchens()}
              disabled={pendingKitchensLoading}
              title="Refresh pending kitchens"
            >
              <RefreshCw size={16} className={pendingKitchensLoading ? 'spinning' : ''} />
              Refresh
            </button>
          </div>

          {pendingKitchensLoading && (
            <div className="homecookt-users-loading">
              <div className="homecookt-users-spinner"></div>
              <span>Loading pending kitchens...</span>
            </div>
          )}

          {pendingKitchensError && !pendingKitchensLoading && (
            <div className="homecookt-users-error">
              <AlertTriangle size={20} />
              <span>{pendingKitchensError}</span>
              <button className="homecookt-users-retry-btn" onClick={() => fetchPendingKitchens()}>
                Retry
              </button>
            </div>
          )}

          {!pendingKitchensLoading && !pendingKitchensError && pendingKitchens.length > 0 && (
            <>
            <div className="homecookt-pending-list">
              {paginatedPending.map((kitchen) => {
                const photoUrl = kitchen.documents?.kitchen_photo && kitchen.documents.kitchen_photo !== 'string'
                  ? kitchen.documents.kitchen_photo
                  : null;
                const location = [kitchen.city, kitchen.state]
                  .filter(v => !isPlaceholder(v))
                  .join(', ') || '—';

                return (
                  <div key={kitchen.kitchen_id} className="homecookt-pending-row">
                    <div className="homecookt-pending-photo-wrap">
                      {photoUrl ? (
                        <img
                          src={photoUrl}
                          alt={kitchen.kitchen_name}
                          className="homecookt-pending-photo"
                        />
                      ) : (
                        <div className="homecookt-pending-photo-fallback">
                          <UtensilsCrossed size={20} />
                        </div>
                      )}
                    </div>

                    <div className="homecookt-pending-info">
                      <span className="homecookt-pending-name">{kitchen.kitchen_name}</span>
                      <span className="homecookt-pending-owner">{sanitize(kitchen.owner?.name)}</span>
                    </div>

                    <div className="homecookt-pending-meta">
                      <span className="homecookt-pending-meta-label">Location</span>
                      <span className="homecookt-pending-meta-value">{location}</span>
                    </div>

                    <div className="homecookt-pending-meta">
                      <span className="homecookt-pending-meta-label">Type</span>
                      <span className="homecookt-pending-meta-value">{sanitize(kitchen.kitchen_type)}</span>
                    </div>

                    <div className="homecookt-pending-meta">
                      <span className="homecookt-pending-meta-label">Contact</span>
                      <span className="homecookt-pending-meta-value">{sanitize(kitchen.owner?.phone)}</span>
                    </div>

                    <div className="homecookt-pending-meta">
                      <span className="homecookt-pending-meta-label">Email</span>
                      <span className="homecookt-pending-meta-value">{kitchen.owner?.email || '—'}</span>
                    </div>

                    <div className="homecookt-pending-meta">
                      <span className="homecookt-pending-meta-label">Submitted</span>
                      <span className="homecookt-pending-meta-value">{formatCreatedAt(kitchen.created_at)}</span>
                    </div>

                    <div className="homecookt-pending-status-col">
                      <span className={`homecookt-kitchen-badge homecookt-kitchen-badge--${kitchen.documents?.verification_status === 'approved' ? 'approved' : kitchen.documents?.verification_status === 'rejected' ? 'rejected' : 'pending'}`}>
                        {sanitize(kitchen.documents?.verification_status || 'Pending')}
                      </span>
                      {(kitchen.documents?.photo_id_url || kitchen.documents?.training_certificate_url) && (
                        <div className="homecookt-doc-chips">
                          {kitchen.documents?.photo_id_url && (
                            <a href={kitchen.documents.photo_id_url} target="_blank" rel="noopener noreferrer" className="homecookt-doc-chip homecookt-doc-chip--id" title="View Photo ID">
                              <FileText size={13} />
                              <span>Photo ID</span>
                              <ExternalLink size={11} />
                            </a>
                          )}
                          {kitchen.documents?.training_certificate_url && (
                            <a href={kitchen.documents.training_certificate_url} target="_blank" rel="noopener noreferrer" className="homecookt-doc-chip homecookt-doc-chip--cert" title="View Training Certificate">
                              <Award size={13} />
                              <span>Certificate</span>
                              <ExternalLink size={11} />
                            </a>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="homecookt-pending-actions">
                      <button
                        className="homecookt-kitchen-action-btn homecookt-kitchen-action-btn--approve"
                        onClick={() => openKitchenActionModal(kitchen, 'approve')}
                        title="Approve kitchen"
                      >
                        <CheckCircle size={14} />
                        Approve
                      </button>
                      <button
                        className="homecookt-kitchen-action-btn homecookt-kitchen-action-btn--reject"
                        onClick={() => openKitchenActionModal(kitchen, 'reject')}
                        title="Reject kitchen"
                      >
                        <Ban size={14} />
                        Reject
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

              {pendingTotalPages > 1 && (
                <div className="homecookt-pagination">
                  <button
                    className="homecookt-pagination-btn homecookt-pagination-btn--nav"
                    onClick={() => handlePendingPageChange(pendingPage - 1)}
                    disabled={pendingPage === 1}
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>

                  <div className="homecookt-pagination-pages">
                    {pendingPage > 3 && (
                      <>
                        <button className="homecookt-pagination-btn" onClick={() => handlePendingPageChange(1)}>1</button>
                        {pendingPage > 4 && <span className="homecookt-pagination-ellipsis">...</span>}
                      </>
                    )}
                    {getPendingPageNumbers().map((pageNum) => (
                      <button
                        key={pageNum}
                        className={`homecookt-pagination-btn ${pageNum === pendingPage ? 'active' : ''}`}
                        onClick={() => handlePendingPageChange(pageNum)}
                      >
                        {pageNum}
                      </button>
                    ))}
                    {pendingPage < pendingTotalPages - 2 && (
                      <>
                        {pendingPage < pendingTotalPages - 3 && <span className="homecookt-pagination-ellipsis">...</span>}
                        <button className="homecookt-pagination-btn" onClick={() => handlePendingPageChange(pendingTotalPages)}>{pendingTotalPages}</button>
                      </>
                    )}
                  </div>

                  <div className="homecookt-pagination-info">Page {pendingPage} of {pendingTotalPages}</div>

                  <button
                    className="homecookt-pagination-btn homecookt-pagination-btn--nav"
                    onClick={() => handlePendingPageChange(pendingPage + 1)}
                    disabled={pendingPage === pendingTotalPages}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}

          {!pendingKitchensLoading && !pendingKitchensError && pendingKitchens.length === 0 && (
            <div className="homecookt-users-empty">
              <UtensilsCrossed size={40} />
              <p>No pending kitchens.</p>
            </div>
          )}
        </div>
        )}

        {/* All Kitchens Section */}
        {activeTab === 'kitchens' && (
        <div className="homecookt-kitchens-section">
          <div className="homecookt-users-header">
            <div className="homecookt-users-title-group">
              <div className="homecookt-users-title-icon">
                <UtensilsCrossed size={22} />
              </div>
              <div>
                <h2 className="homecookt-users-title">All Kitchens</h2>
                {kitchens.data.length > 0 && !kitchens.loading && (
                  <p className="homecookt-users-count">
                    {kitchensTotal} total — page {kitchens.page} of {kitchens.totalPages}{kitchens.hasNextPage ? '+' : ''}
                  </p>
                )}
              </div>
            </div>
            <button
              className="homecookt-users-refresh-btn"
              onClick={() => { kitchens.resetPagination(); kitchens.setPage(1); }}
              disabled={kitchens.loading}
              title="Refresh kitchens"
            >
              <RefreshCw size={16} className={kitchens.loading ? 'spinning' : ''} />
              Refresh
            </button>
          </div>

          {kitchens.loading && (
            <div className="homecookt-users-loading">
              <div className="homecookt-users-spinner"></div>
              <span>Loading kitchens...</span>
            </div>
          )}

          {kitchens.error && !kitchens.loading && (
            <div className="homecookt-users-error">
              <AlertTriangle size={20} />
              <span>{kitchens.error}</span>
              <button
                className="homecookt-users-retry-btn"
                onClick={() => kitchens.fetch(kitchens.page)}
              >
                Retry
              </button>
            </div>
          )}

          {!kitchens.loading && !kitchens.error && kitchens.data.length > 0 && (
            <>
              <div className="homecookt-users-table-wrapper">
                <table className="homecookt-users-table homecookt-kitchens-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>ID</th>
                      <th>Kitchen Name</th>
                      <th>Owner</th>
                      <th>City / State</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {kitchens.data.map((kitchen) => {
                      const photoUrl = kitchen.kitchen_photo && kitchen.kitchen_photo !== 'string'
                        ? kitchen.kitchen_photo
                        : null;
                      const location = [kitchen.city, kitchen.state]
                        .filter(v => !isPlaceholder(v))
                        .join(', ') || '—';
                      return (
                        <tr key={kitchen.kitchen_id}>
                          <td className="homecookt-kitchens-td-photo">
                            {photoUrl ? (
                              <img src={photoUrl} alt={kitchen.kitchen_name} className="homecookt-kitchens-table-photo" />
                            ) : (
                              <div className="homecookt-kitchens-table-photo-fallback">
                                <UtensilsCrossed size={16} />
                              </div>
                            )}
                          </td>
                          <td className="homecookt-users-td-id">#{kitchen.kitchen_id}</td>
                          <td className="homecookt-users-td-email">{kitchen.kitchen_name}</td>
                          <td>
                            <div className="homecookt-kitchen-owner-cell">
                              <span>{sanitize(kitchen.owner_name)}</span>
                              {kitchen.owner_is_blocked && (
                                <span className="homecookt-kitchen-badge homecookt-kitchen-badge--inactive" style={{ fontSize: '0.65rem', padding: '0.1rem 0.4rem' }}>Owner Blocked</span>
                              )}
                              {!kitchen.owner_is_blocked && !kitchen.owner_is_active && (
                                <span className="homecookt-kitchen-badge homecookt-kitchen-badge--pending" style={{ fontSize: '0.65rem', padding: '0.1rem 0.4rem' }}>Owner Inactive</span>
                              )}
                            </div>
                          </td>
                          <td>{location}</td>
                          <td className="homecookt-users-td-date">{sanitize(kitchen.kitchen_type)}</td>
                          <td>
                            <div className="homecookt-kitchens-status-stack">
                              {!kitchen.is_approved ? (
                                <span className="homecookt-kitchen-badge homecookt-kitchen-badge--pending">Pending Approval</span>
                              ) : kitchen.is_blocked ? (
                                <span className="homecookt-kitchen-badge homecookt-kitchen-badge--inactive">Approved / Blocked</span>
                              ) : !kitchen.is_online ? (
                                <span className="homecookt-kitchen-badge homecookt-kitchen-badge--pending">Approved / Offline</span>
                              ) : (
                                <span className="homecookt-kitchen-badge homecookt-kitchen-badge--active">Approved / Online</span>
                              )}
                            </div>
                          </td>
                          <td className="homecookt-users-td-date">{formatCreatedAt(kitchen.created_at)}</td>
                          <td>
                            <div className="homecookt-kitchen-actions-stack">
                              <button
                                className="homecookt-kitchen-action-btn homecookt-kitchen-action-btn--view"
                                onClick={() => { setSelectedKitchen(kitchen); setShowKitchenDetailModal(true); }}
                                title="View details"
                              >
                                <ClipboardList size={14} />
                                View
                              </button>
                              {!kitchen.is_blocked ? (
                                <button
                                  className="homecookt-kitchen-action-btn homecookt-kitchen-action-btn--reject"
                                  onClick={() => openBlockKitchenModal(kitchen, 'block')}
                                  title="Block kitchen"
                                >
                                  <Ban size={14} />
                                  Block
                                </button>
                              ) : (
                                <button
                                  className="homecookt-kitchen-action-btn homecookt-kitchen-action-btn--approve"
                                  onClick={() => openBlockKitchenModal(kitchen, 'unblock')}
                                  title="Unblock kitchen"
                                >
                                  <ShieldCheck size={14} />
                                  Unblock
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {(kitchens.totalPages > 1 || kitchens.hasNextPage || kitchens.hasPrevPage) && (
                <div className="homecookt-pagination">
                  <button
                    className="homecookt-pagination-btn homecookt-pagination-btn--nav"
                    onClick={() => kitchens.handlePageChange(kitchens.page - 1, '.homecookt-kitchens-section')}
                    disabled={!kitchens.hasPrevPage}
                  >
                    <ChevronLeft size={16} />
                    Previous
                  </button>

                  <div className="homecookt-pagination-pages">
                    {kitchens.page > 3 && (
                      <>
                        <button className="homecookt-pagination-btn" onClick={() => kitchens.handlePageChange(1, '.homecookt-kitchens-section')}>1</button>
                        {kitchens.page > 4 && <span className="homecookt-pagination-ellipsis">…</span>}
                      </>
                    )}
                    {kitchens.getPageNumbers().map((pageNum) => (
                      <button
                        key={pageNum}
                        className={`homecookt-pagination-btn ${pageNum === kitchens.page ? 'active' : ''}`}
                        onClick={() => kitchens.handlePageChange(pageNum, '.homecookt-kitchens-section')}
                      >
                        {pageNum}
                      </button>
                    ))}
                    {kitchens.page < kitchens.totalPages - 2 && (
                      <>
                        {kitchens.page < kitchens.totalPages - 3 && <span className="homecookt-pagination-ellipsis">…</span>}
                        <button className="homecookt-pagination-btn" onClick={() => kitchens.handlePageChange(kitchens.totalPages, '.homecookt-kitchens-section')}>{kitchens.totalPages}</button>
                      </>
                    )}
                  </div>

                  <div className="homecookt-pagination-info">Page {kitchens.page} of {kitchens.totalPages}{kitchens.hasNextPage ? '+' : ''}</div>

                  <button
                    className="homecookt-pagination-btn homecookt-pagination-btn--nav"
                    onClick={() => kitchens.handlePageChange(kitchens.page + 1, '.homecookt-kitchens-section')}
                    disabled={!kitchens.hasNextPage}
                  >
                    Next
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}

          {!kitchens.loading && !kitchens.error && kitchens.data.length === 0 && (
            <div className="homecookt-users-empty">
              <UtensilsCrossed size={40} />
              <p>No kitchens found.</p>
            </div>
          )}
        </div>
        )}

        {/* All Foods Section */}
        {activeTab === 'foods' && (
        <div className="homecookt-foods-section">
          <div className="homecookt-users-header">
            <div className="homecookt-users-title-group">
              <div className="homecookt-users-title-icon">
                <ShoppingBag size={22} />
              </div>
              <div>
                <h2 className="homecookt-users-title">All Foods</h2>
                {foods.data.length > 0 && !foods.loading && (
                  <p className="homecookt-users-count">
                    Showing {foods.data.length} foods — page {foods.page}{foods.hasNextPage ? '+' : ''}
                  </p>
                )}
              </div>
            </div>
            <button
              className="homecookt-users-refresh-btn"
              onClick={() => { foods.resetPagination(); foods.setPage(1); }}
              disabled={foods.loading}
              title="Refresh foods"
            >
              <RefreshCw size={16} className={foods.loading ? 'spinning' : ''} />
              Refresh
            </button>
          </div>

          {foods.loading && (
            <div className="homecookt-users-loading">
              <div className="homecookt-users-spinner"></div>
              <span>Loading foods...</span>
            </div>
          )}

          {foods.error && !foods.loading && (
            <div className="homecookt-users-error">
              <AlertTriangle size={20} />
              <span>{foods.error}</span>
              <button
                className="homecookt-users-retry-btn"
                onClick={() => foods.fetch(foods.page)}
              >
                Retry
              </button>
            </div>
          )}

          {!foods.loading && !foods.error && foods.data.length > 0 && (
            <>
              <div className="homecookt-users-table-wrapper">
                <table className="homecookt-users-table homecookt-foods-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>ID</th>
                      <th>Food Name</th>
                      <th>Category</th>
                      <th>Price</th>
                      <th>Serving</th>
                      <th>Qty</th>
                      <th>Kitchen</th>
                      <th>Status</th>
                      <th>Engagement</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {foods.data.map((food) => {
                      const firstImage = getFirstImageUrl(food.image_urls);
                      return (
                        <tr key={food.food_id}>
                          <td className="homecookt-foods-td-photo">
                            {firstImage && firstImage !== 'https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg' ? (
                              <img src={firstImage} alt={food.food_name} className="homecookt-foods-table-photo" />
                            ) : (
                              <div className="homecookt-foods-table-photo-fallback">
                                <ShoppingBag size={16} />
                              </div>
                            )}
                          </td>
                          <td className="homecookt-users-td-id">#{food.food_id}</td>
                          <td className="homecookt-users-td-email">{isPlaceholder(food.food_name) ? '—' : food.food_name}</td>
                          <td>{sanitize(food.category)}</td>
                          <td className="homecookt-foods-td-price">
                            {food.price > 0 ? `£${food.price.toFixed(2)}` : '—'}
                          </td>
                          <td>{sanitize(food.serving_size)}</td>
                          <td>{food.available_quantity}</td>
                          <td className="homecookt-users-td-id">#{food.kitchen_id}</td>
                          <td>
                            {food.is_available ? (
                              <span className="homecookt-food-badge homecookt-food-badge--available">Available</span>
                            ) : (
                              <span className="homecookt-food-badge homecookt-food-badge--unavailable">Unavailable</span>
                            )}
                          </td>
                          <td>
                            <div className="homecookt-food-engagement-cell">
                              {food.is_featured && (
                                <span className="homecookt-food-badge homecookt-food-badge--featured" title="Featured">★</span>
                              )}
                              {food.is_bestseller && (
                                <span className="homecookt-food-badge homecookt-food-badge--bestseller" title="Bestseller">Bestseller</span>
                              )}
                              {food.is_recommended && (
                                <span className="homecookt-food-badge homecookt-food-badge--recommended" title="Recommended">Recommended</span>
                              )}
                              {food.discount_percentage > 0 && (
                                <span className="homecookt-food-badge homecookt-food-badge--discount" title="Discount">{food.discount_percentage}% off</span>
                              )}
                              {food.average_rating > 0 && (
                                <span className="homecookt-food-badge homecookt-food-badge--rating" title="Rating">★ {food.average_rating.toFixed(1)} ({food.total_reviews})</span>
                              )}
                              {food.total_orders > 0 && (
                                <span className="homecookt-food-badge homecookt-food-badge--orders" title="Total orders">{food.total_orders} orders</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <button
                              className="homecookt-food-remove-btn"
                              onClick={() => openRemoveFoodModal(food)}
                              title="Remove food"
                            >
                              <Trash2 size={14} />
                              Remove
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {foods.totalPages > 1 && (
                <div className="homecookt-pagination">
                  <button
                    className="homecookt-pagination-btn homecookt-pagination-btn--nav"
                    onClick={() => foods.handlePageChange(foods.page - 1, '.homecookt-foods-section')}
                    disabled={foods.page === 1}
                  >
                    <ChevronLeft size={16} />
                    <span>Previous</span>
                  </button>

                  <div className="homecookt-pagination-pages">
                    {foods.page > 3 && (
                      <>
                        <button className="homecookt-pagination-btn" onClick={() => foods.handlePageChange(1, '.homecookt-foods-section')}>1</button>
                        {foods.page > 4 && <span className="homecookt-pagination-ellipsis">...</span>}
                      </>
                    )}
                    {foods.getPageNumbers().map((pageNum) => (
                      <button
                        key={pageNum}
                        className={`homecookt-pagination-btn ${pageNum === foods.page ? 'active' : ''}`}
                        onClick={() => foods.handlePageChange(pageNum, '.homecookt-foods-section')}
                      >
                        {pageNum}
                      </button>
                    ))}
                    {foods.page < foods.totalPages - 2 && (
                      <>
                        {foods.page < foods.totalPages - 3 && <span className="homecookt-pagination-ellipsis">...</span>}
                        <button className="homecookt-pagination-btn" onClick={() => foods.handlePageChange(foods.totalPages, '.homecookt-foods-section')}>{foods.totalPages}</button>
                      </>
                    )}
                  </div>

                  <div className="homecookt-pagination-info">Page {foods.page} of {foods.totalPages}</div>

                  <button
                    className="homecookt-pagination-btn homecookt-pagination-btn--nav"
                    onClick={() => foods.handlePageChange(foods.page + 1, '.homecookt-foods-section')}
                    disabled={!foods.hasNextPage}
                  >
                    <span>Next</span>
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}

          {!foods.loading && !foods.error && foods.data.length === 0 && (
            <div className="homecookt-users-empty">
              <ShoppingBag size={40} />
              <p>No foods found.</p>
            </div>
          )}
        </div>
        )}

        {/* Audit Logs Section */}
        {activeTab === 'audit' && (
        <div className="homecookt-users-section">
          <div className="homecookt-users-header">
            <div className="homecookt-users-title-group">
              <div className="homecookt-users-title-icon">
                <ClipboardList size={22} />
              </div>
              <div>
                <h2 className="homecookt-users-title">Audit Logs</h2>
                {auditLogs.data.length > 0 && !auditLogs.loading && (
                  <p className="homecookt-users-count">
                    Showing {auditLogs.data.length} logs — page {auditLogs.page}{auditLogs.hasNextPage ? '+' : ''}
                  </p>
                )}
              </div>
            </div>
            <button
              className="homecookt-users-refresh-btn"
              onClick={() => auditLogs.fetch(auditLogs.page)}
              disabled={auditLogs.loading}
              title="Refresh audit logs"
            >
              <RefreshCw size={16} className={auditLogs.loading ? 'spinning' : ''} />
              Refresh
            </button>
          </div>

          {auditLogs.loading && (
            <div className="homecookt-users-loading">
              <RefreshCw size={24} className="spinning" />
              <p>Loading audit logs...</p>
            </div>
          )}

          {auditLogs.error && !auditLogs.loading && (
            <div className="homecookt-users-error">
              <AlertTriangle size={20} />
              <p>{auditLogs.error}</p>
            </div>
          )}

          {!auditLogs.loading && !auditLogs.error && auditLogs.data.length > 0 && (
            <>
              <div className="homecookt-users-table-container">
                <table className="homecookt-users-table">
                  <thead>
                    <tr>
                      <th>Admin</th>
                      <th>Action</th>
                      <th>Target</th>
                      <th>Details</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.data.map((log) => (
                      <tr key={log._id}>
                        <td className="homecookt-users-td-id">#{log.admin_id}</td>
                        <td>
                          <span className="homecookt-audit-action-badge">
                            {log.action.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td>
                          <span className="homecookt-audit-target">
                            {log.target_type} #{log.target_id}
                          </span>
                        </td>
                        <td className="homecookt-audit-details">{log.details}</td>
                        <td className="homecookt-audit-date">
                          {new Date(log.created_at).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric',
                          })}{' '}
                          {new Date(log.created_at).toLocaleTimeString('en-GB', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {auditLogs.totalPages > 1 && (
                <div className="homecookt-pagination">
                  <button
                    className="homecookt-pagination-btn"
                    onClick={() => auditLogs.setPage(Math.max(1, auditLogs.page - 1))}
                    disabled={auditLogs.page === 1}
                  >
                    <ChevronLeft size={16} />
                  </button>
                  {Array.from({ length: auditLogs.totalPages }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      className={`homecookt-pagination-btn ${p === auditLogs.page ? 'active' : ''}`}
                      onClick={() => auditLogs.setPage(p)}
                    >
                      {p}
                    </button>
                  ))}
                  <button
                    className="homecookt-pagination-btn"
                    onClick={() => auditLogs.setPage(auditLogs.page + 1)}
                    disabled={!auditLogs.hasNextPage}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}

          {!auditLogs.loading && !auditLogs.error && auditLogs.data.length === 0 && (
            <div className="homecookt-users-empty">
              <ClipboardList size={40} />
              <p>No audit logs found.</p>
            </div>
          )}
        </div>
        )}

        {/* Promotions Section */}
        {activeTab === 'promotions' && (
        <div className="homecookt-users-section">
          <div className="homecookt-users-header">
            <div className="homecookt-users-title-group">
              <div className="homecookt-users-title-icon homecookt-promo-icon">
                <Megaphone size={22} />
              </div>
              <div>
                <h2 className="homecookt-users-title">Send Promotion</h2>
                <p className="homecookt-users-count">Broadcast a notification to targeted users</p>
              </div>
            </div>
          </div>

          <div className="homecookt-promo-form-container">
            <form
              className="homecookt-promo-form"
              onSubmit={async (e) => {
                e.preventDefault();
                setPromoSending(true);
                setPromoError('');
                setPromoSuccess('');

                const token = localStorage.getItem('adminToken');
                const formData = new FormData();
                formData.append('title', promoTitle);
                formData.append('message', promoMessage);
                if (promoBanner) formData.append('banner', promoBanner);
                if (promoCity.trim()) formData.append('city', promoCity.trim());
                if (promoState.trim()) formData.append('state', promoState.trim());
                if (promoCountry.trim()) formData.append('country', promoCountry.trim());
                if (promoRole.trim()) formData.append('role', promoRole.trim());
                if (promoDietaryPreference.trim()) formData.append('dietary_preference', promoDietaryPreference.trim());
                formData.append('send_to_all', String(promoSendToAll));
                if (promoInactiveDays.trim()) formData.append('inactive_days', promoInactiveDays.trim());
                if (promoFavoriteCategory.trim()) formData.append('favorite_category', promoFavoriteCategory.trim());
                if (promoScheduleAt.trim()) formData.append('schedule_at', promoScheduleAt.trim());

                try {
                  const response = await fetch(ADMIN_SEND_PROMOTION_URL, {
                    method: 'POST',
                    headers: {
                      'Accept': 'application/json',
                      'Authorization': `Bearer ${token}`,
                    },
                    body: formData,
                  });

                  const data = await response.json().catch(() => ({})) as any;

                  if (response.ok) {
                    const stats = data.stats || {};
                    const failureReasons = data.failure_reasons || [];
                    let successMsg = `Promotion sent to ${stats.sent || 0} user${(stats.sent || 0) === 1 ? '' : 's'}.`;
                    if (stats.failed > 0) {
                      successMsg += ` ${stats.failed} failed.`;
                    }
                    if (stats.total_matched != null) {
                      successMsg += ` Matched: ${stats.total_matched}.`;
                    }
                    if (failureReasons.length > 0 && failureReasons.length <= 5) {
                      const reasons = failureReasons.map((f: any) => `User #${f.user_id}: ${f.reason}`).join('; ');
                      successMsg += ` Failures: ${reasons}.`;
                    }
                    setPromoSuccess(successMsg);
                    setPromoTitle('');
                    setPromoMessage('');
                    setPromoBanner(null);
                    setPromoBannerPreview('');
                    setPromoCity('');
                    setPromoState('');
                    setPromoCountry('');
                    setPromoRole('');
                    setPromoDietaryPreference('');
                    setPromoSendToAll(false);
                    setPromoInactiveDays('');
                    setPromoFavoriteCategory('');
                    setPromoScheduleAt('');
                    if (promoBannerInputRef.current) promoBannerInputRef.current.value = '';
                  } else if (response.status === 422) {
                    const msgs = Array.isArray(data.detail)
                      ? data.detail.map((d: any) => d.msg).join('. ')
                      : 'Validation error. Please check your inputs.';
                    setPromoError(msgs);
                  } else if (response.status === 401 || response.status === 403) {
                    setPromoError('Session expired or access denied. Please log out and log in again.');
                  } else if (response.status >= 500) {
                    setPromoError('Server error. Please try again later.');
                  } else {
                    setPromoError(data.detail || data.message || 'Failed to send promotion. Please try again.');
                  }
                } catch {
                  setPromoError('Network error. Please check your connection and try again.');
                } finally {
                  setPromoSending(false);
                }
              }}
            >
              {/* Required fields */}
              <div className="homecookt-promo-section-label">Required</div>

              <div className="homecookt-promo-field">
                <label htmlFor="promo-title" className="homecookt-promo-label">
                  Title <span className="homecookt-promo-required">*</span>
                </label>
                <input
                  id="promo-title"
                  type="text"
                  className="homecookt-promo-input"
                  placeholder="e.g. Weekend Special Offer"
                  value={promoTitle}
                  onChange={(e) => { setPromoTitle(e.target.value); setPromoError(''); setPromoSuccess(''); }}
                  required
                />
              </div>

              <div className="homecookt-promo-field">
                <label htmlFor="promo-message" className="homecookt-promo-label">
                  Message <span className="homecookt-promo-required">*</span>
                </label>
                <textarea
                  id="promo-message"
                  className="homecookt-promo-textarea"
                  placeholder="Write the promotion message that users will receive..."
                  value={promoMessage}
                  onChange={(e) => { setPromoMessage(e.target.value); setPromoError(''); setPromoSuccess(''); }}
                  rows={4}
                  required
                />
              </div>

              {/* Optional fields */}
              <div className="homecookt-promo-section-label homecookt-promo-section-label--optional">Optional Targeting &amp; Settings</div>

              <div className="homecookt-promo-field">
                <label htmlFor="promo-banner" className="homecookt-promo-label">
                  Banner Image
                </label>
                <div className="homecookt-promo-file-area">
                  <input
                    ref={promoBannerInputRef}
                    id="promo-banner"
                    type="file"
                    accept="image/*"
                    className="homecookt-promo-file-input"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      setPromoBanner(file);
                      if (file) {
                        const url = URL.createObjectURL(file);
                        setPromoBannerPreview(url);
                      } else {
                        setPromoBannerPreview('');
                      }
                    }}
                  />
                  <label htmlFor="promo-banner" className="homecookt-promo-file-label">
                    <ImagePlus size={18} />
                    <span>{promoBanner ? promoBanner.name : 'Choose image...'}</span>
                  </label>
                  {promoBannerPreview && (
                    <div className="homecookt-promo-banner-preview">
                      <img src={promoBannerPreview} alt="Banner preview" />
                      <button
                        type="button"
                        className="homecookt-promo-banner-remove"
                        onClick={() => {
                          setPromoBanner(null);
                          setPromoBannerPreview('');
                          if (promoBannerInputRef.current) promoBannerInputRef.current.value = '';
                        }}
                      >
                        <X size={14} /> Remove
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="homecookt-promo-grid">
                <div className="homecookt-promo-field">
                  <label htmlFor="promo-city" className="homecookt-promo-label">
                    Target City
                  </label>
                  <input
                    id="promo-city"
                    type="text"
                    className="homecookt-promo-input"
                    placeholder="e.g. London"
                    value={promoCity}
                    onChange={(e) => setPromoCity(e.target.value)}
                  />
                  <span className="homecookt-promo-hint">Leave blank to target all cities</span>
                </div>

                <div className="homecookt-promo-field">
                  <label htmlFor="promo-state" className="homecookt-promo-label">
                    Target State
                  </label>
                  <input
                    id="promo-state"
                    type="text"
                    className="homecookt-promo-input"
                    placeholder="e.g. Tamil Nadu"
                    value={promoState}
                    onChange={(e) => setPromoState(e.target.value)}
                  />
                  <span className="homecookt-promo-hint">Leave blank to target all states</span>
                </div>

                <div className="homecookt-promo-field">
                  <label htmlFor="promo-country" className="homecookt-promo-label">
                    Target Country
                  </label>
                  <input
                    id="promo-country"
                    type="text"
                    className="homecookt-promo-input"
                    placeholder="e.g. India"
                    value={promoCountry}
                    onChange={(e) => setPromoCountry(e.target.value)}
                  />
                  <span className="homecookt-promo-hint">Leave blank to target all countries</span>
                </div>

                <div className="homecookt-promo-field">
                  <label htmlFor="promo-role" className="homecookt-promo-label">
                    Target Role
                  </label>
                  <select
                    id="promo-role"
                    className="homecookt-promo-input"
                    value={promoRole}
                    onChange={(e) => setPromoRole(e.target.value)}
                  >
                    <option value="">All roles</option>
                    <option value="customer">Customer</option>
                    <option value="kitchen_owner">Kitchen Owner</option>
                    <option value="delivery_partner">Delivery Partner</option>
                  </select>
                  <span className="homecookt-promo-hint">Filter by user role</span>
                </div>

                <div className="homecookt-promo-field">
                  <label htmlFor="promo-dietary" className="homecookt-promo-label">
                    Dietary Preference
                  </label>
                  <select
                    id="promo-dietary"
                    className="homecookt-promo-input"
                    value={promoDietaryPreference}
                    onChange={(e) => setPromoDietaryPreference(e.target.value)}
                  >
                    <option value="">Any preference</option>
                    <option value="Vegetarian">Vegetarian</option>
                    <option value="Non Vegetarian">Non Vegetarian</option>
                    <option value="Vegan">Vegan</option>
                    <option value="Eggetarian">Eggetarian</option>
                  </select>
                  <span className="homecookt-promo-hint">Filter by dietary preference</span>
                </div>

                <div className="homecookt-promo-field">
                  <label htmlFor="promo-inactive" className="homecookt-promo-label">
                    Inactive for (days)
                  </label>
                  <input
                    id="promo-inactive"
                    type="number"
                    min="1"
                    className="homecookt-promo-input"
                    placeholder="e.g. 30"
                    value={promoInactiveDays}
                    onChange={(e) => setPromoInactiveDays(e.target.value)}
                  />
                  <span className="homecookt-promo-hint">Target users inactive for N days</span>
                </div>

                <div className="homecookt-promo-field">
                  <label htmlFor="promo-category" className="homecookt-promo-label">
                    Favorite Category
                  </label>
                  <input
                    id="promo-category"
                    type="text"
                    className="homecookt-promo-input"
                    placeholder="e.g. Indian"
                    value={promoFavoriteCategory}
                    onChange={(e) => setPromoFavoriteCategory(e.target.value)}
                  />
                  <span className="homecookt-promo-hint">Target by food preference</span>
                </div>

                <div className="homecookt-promo-field">
                  <label htmlFor="promo-schedule" className="homecookt-promo-label">
                    Schedule At
                  </label>
                  <input
                    id="promo-schedule"
                    type="datetime-local"
                    className="homecookt-promo-input"
                    value={promoScheduleAt}
                    onChange={(e) => setPromoScheduleAt(e.target.value)}
                  />
                  <span className="homecookt-promo-hint">Leave blank to send immediately</span>
                </div>

                <div className="homecookt-promo-field homecookt-promo-field--full">
                  <label className="homecookt-promo-checkbox-label">
                    <input
                      type="checkbox"
                      checked={promoSendToAll}
                      onChange={(e) => setPromoSendToAll(e.target.checked)}
                    />
                    Send to ALL users regardless of filters
                  </label>
                </div>
              </div>

              {promoError && (
                <div className="homecookt-promo-feedback homecookt-promo-feedback--error">
                  <AlertTriangle size={16} />
                  <span>{promoError}</span>
                </div>
              )}

              {promoSuccess && (
                <div className="homecookt-promo-feedback homecookt-promo-feedback--success">
                  <CheckCircle size={16} />
                  <span>{promoSuccess}</span>
                </div>
              )}

              <div className="homecookt-promo-actions">
                <button
                  type="submit"
                  className="homecookt-promo-submit"
                  disabled={promoSending}
                >
                  {promoSending ? (
                    <>
                      <RefreshCw size={16} className="spinning" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Send Promotion
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          <div className="homecookt-debug-push-section">
            <div className="homecookt-debug-push-header">
              <h3>Push Notification Diagnostics</h3>
              <p>Run a full end-to-end diagnostic to verify Firebase Cloud Messaging health.</p>
            </div>
            <button
              className="homecookt-debug-push-btn"
              onClick={handleDebugPush}
              disabled={debugPushLoading}
            >
              {debugPushLoading ? (
                <>
                  <RefreshCw size={16} className="spinning" />
                  Running diagnostic...
                </>
              ) : (
                <>
                  <Send size={16} />
                  Run Diagnostic
                </>
              )}
            </button>

            {debugPushError && (
              <div className="homecookt-promo-feedback homecookt-promo-feedback--error">
                <AlertTriangle size={16} />
                <span>{debugPushError}</span>
              </div>
            )}

            {debugPushResult && (
              <div className="homecookt-debug-push-result">
                <div className="homecookt-debug-push-summary">
                  <div className="homecookt-debug-push-stat">
                    <span className="homecookt-debug-push-stat-label">Firebase</span>
                    <span className="homecookt-debug-push-stat-value">
                      {debugPushResult.firebase_initialized ? 'Initialized' : 'Not initialized'}
                    </span>
                  </div>
                  <div className="homecookt-debug-push-stat">
                    <span className="homecookt-debug-push-stat-label">Project</span>
                    <span className="homecookt-debug-push-stat-value">{debugPushResult.firebase_project_id || '—'}</span>
                  </div>
                  <div className="homecookt-debug-push-stat">
                    <span className="homecookt-debug-push-stat-label">Users in DB</span>
                    <span className="homecookt-debug-push-stat-value">{debugPushResult.users_in_db ?? '—'}</span>
                  </div>
                  <div className="homecookt-debug-push-stat">
                    <span className="homecookt-debug-push-stat-label">Users with token</span>
                    <span className="homecookt-debug-push-stat-value">{debugPushResult.users_with_token ?? '—'}</span>
                  </div>
                </div>

                {Array.isArray(debugPushResult.test_sends) && debugPushResult.test_sends.length > 0 && (
                  <div className="homecookt-debug-push-table-wrap">
                    <table className="homecookt-debug-push-table">
                      <thead>
                        <tr>
                          <th>User</th>
                          <th>Email</th>
                          <th>Status</th>
                          <th>Message ID</th>
                          <th>Error</th>
                        </tr>
                      </thead>
                      <tbody>
                        {debugPushResult.test_sends.map((ts: any, idx: number) => (
                          <tr key={idx}>
                            <td>#{ts.user_id}</td>
                            <td>{ts.email || '—'}</td>
                            <td>
                              {ts.success ? (
                                <span className="homecookt-food-badge homecookt-food-badge--available">Success</span>
                              ) : (
                                <span className="homecookt-food-badge homecookt-food-badge--unavailable">Failed</span>
                              )}
                            </td>
                            <td className="homecookt-debug-push-msg-id">{ts.fcm_message_id || '—'}</td>
                            <td>{ts.error || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        )}
      </main>

      {showRemoveFoodModal && foodToRemove && (
        <div className="homecookt-modal-overlay" onClick={closeRemoveFoodModal}>
          <div className="homecookt-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="homecookt-modal-close" onClick={closeRemoveFoodModal}>
              <X size={18} />
            </button>
            <div className="homecookt-modal-header homecookt-modal-header--danger">
              <Trash2 size={22} />
              <h3>Remove Food</h3>
            </div>
            <div className="homecookt-modal-body">
              <p className="homecookt-modal-desc">
                You are about to remove <strong>{foodToRemove.food_name}</strong> (ID #{foodToRemove.food_id}).
                This action cannot be undone.
              </p>
              <label className="homecookt-modal-label" htmlFor="remove-food-reason">
                Reason for removal <span className="homecookt-modal-required">*</span>
              </label>
              <input
                id="remove-food-reason"
                type="text"
                className="homecookt-modal-input"
                placeholder="e.g. Violates food safety guidelines"
                value={removeFoodReason}
                onChange={(e) => setRemoveFoodReason(e.target.value)}
                disabled={removingFood}
                autoFocus
              />
              {removeFoodError && (
                <p className="homecookt-modal-error">
                  <AlertTriangle size={14} />
                  {removeFoodError}
                </p>
              )}
            </div>
            <div className="homecookt-modal-actions">
              <button
                className="homecookt-modal-btn homecookt-modal-btn--cancel"
                onClick={closeRemoveFoodModal}
                disabled={removingFood}
              >
                Cancel
              </button>
              <button
                className="homecookt-modal-btn homecookt-modal-btn--danger"
                onClick={handleRemoveFood}
                disabled={removingFood || !removeFoodReason.trim()}
              >
                {removingFood ? 'Removing...' : 'Confirm Remove'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBlockUserModal && userToBlock && (
        <div className="homecookt-modal-overlay" onClick={closeBlockUserModal}>
          <div className="homecookt-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="homecookt-modal-close" onClick={closeBlockUserModal}>
              <X size={18} />
            </button>
            <div className={`homecookt-modal-header ${blockAction === 'block' ? 'homecookt-modal-header--warning' : 'homecookt-modal-header--success'}`}>
              {blockAction === 'block' ? <Ban size={22} /> : <ShieldCheck size={22} />}
              <h3>{blockAction === 'block' ? 'Block User' : 'Unblock User'}</h3>
            </div>
            <div className="homecookt-modal-body">
              <p className="homecookt-modal-desc">
                {blockAction === 'block' ? (
                  <>Are you sure you want to block <strong>{userToBlock.email}</strong> (ID #{userToBlock.user_id})? They will no longer be able to access the platform.</>
                ) : (
                  <>Are you sure you want to unblock <strong>{userToBlock.email}</strong> (ID #{userToBlock.user_id})? They will regain access to the platform.</>
                )}
              </p>
              {blockUserError && (
                <p className="homecookt-modal-error">
                  <AlertTriangle size={14} />
                  {blockUserError}
                </p>
              )}
            </div>
            <div className="homecookt-modal-actions">
              <button
                className="homecookt-modal-btn homecookt-modal-btn--cancel"
                onClick={closeBlockUserModal}
                disabled={blockingUser}
              >
                Cancel
              </button>
              <button
                className={`homecookt-modal-btn ${blockAction === 'block' ? 'homecookt-modal-btn--danger' : 'homecookt-modal-btn--success'}`}
                onClick={handleBlockUser}
                disabled={blockingUser}
              >
                {blockingUser
                  ? (blockAction === 'block' ? 'Blocking...' : 'Unblocking...')
                  : (blockAction === 'block' ? 'Confirm Block' : 'Confirm Unblock')
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {showKitchenActionModal && kitchenToAction && (
        <div className="homecookt-modal-overlay" onClick={closeKitchenActionModal}>
          <div className="homecookt-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="homecookt-modal-close" onClick={closeKitchenActionModal}>
              <X size={18} />
            </button>
            <div className={`homecookt-modal-header ${kitchenAction === 'approve' ? 'homecookt-modal-header--success' : 'homecookt-modal-header--danger'}`}>
              {kitchenAction === 'approve' ? <CheckCircle size={22} /> : <Ban size={22} />}
              <h3>{kitchenAction === 'approve' ? 'Approve Kitchen' : 'Reject Kitchen'}</h3>
            </div>
            <div className="homecookt-modal-body">
              <p className="homecookt-modal-desc">
                {kitchenAction === 'approve' ? (
                  <>Are you sure you want to approve <strong>{kitchenToAction.kitchen_name}</strong> (ID #{kitchenToAction.kitchen_id})? It will become visible on the platform.</>
                ) : (
                  <>You are about to reject <strong>{kitchenToAction.kitchen_name}</strong> (ID #{kitchenToAction.kitchen_id}). Please provide a reason.</>
                )}
              </p>
              {kitchenAction === 'reject' && (
                <>
                  <label className="homecookt-modal-label" htmlFor="reject-kitchen-reason">
                    Reason for rejection <span className="homecookt-modal-required">*</span>
                  </label>
                  <input
                    id="reject-kitchen-reason"
                    type="text"
                    className="homecookt-modal-input"
                    placeholder="e.g. Not up to the standards"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    disabled={kitchenActioning}
                    autoFocus
                  />
                </>
              )}
              {kitchenActionError && (
                <p className="homecookt-modal-error">
                  <AlertTriangle size={14} />
                  {kitchenActionError}
                </p>
              )}
            </div>
            <div className="homecookt-modal-actions">
              <button
                className="homecookt-modal-btn homecookt-modal-btn--cancel"
                onClick={closeKitchenActionModal}
                disabled={kitchenActioning}
              >
                Cancel
              </button>
              <button
                className={`homecookt-modal-btn ${kitchenAction === 'approve' ? 'homecookt-modal-btn--success' : 'homecookt-modal-btn--danger'}`}
                onClick={handleKitchenAction}
                disabled={kitchenActioning || (kitchenAction === 'reject' && !rejectReason.trim())}
              >
                {kitchenActioning
                  ? (kitchenAction === 'approve' ? 'Approving...' : 'Rejecting...')
                  : (kitchenAction === 'approve' ? 'Confirm Approve' : 'Confirm Reject')
                }
              </button>
            </div>
          </div>
        </div>
      )}

      {showBlockKitchenModal && kitchenToBlock && (
        <div className="homecookt-modal-overlay" onClick={closeBlockKitchenModal}>
          <div className="homecookt-modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="homecookt-modal-close" onClick={closeBlockKitchenModal}>
              <X size={18} />
            </button>
            <div className={`homecookt-modal-header ${blockKitchenAction === 'block' ? 'homecookt-modal-header--warning' : 'homecookt-modal-header--success'}`}>
              {blockKitchenAction === 'block' ? <Ban size={22} /> : <ShieldCheck size={22} />}
              <h3>{blockKitchenAction === 'block' ? 'Block Kitchen' : 'Unblock Kitchen'}</h3>
            </div>
            <div className="homecookt-modal-body">
              <p className="homecookt-modal-desc">
                {blockKitchenAction === 'block' ? (
                  <>Are you sure you want to block <strong>{kitchenToBlock.kitchen_name}</strong> (ID #{kitchenToBlock.kitchen_id})? It will no longer be visible on the platform.</>
                ) : (
                  <>Are you sure you want to unblock <strong>{kitchenToBlock.kitchen_name}</strong> (ID #{kitchenToBlock.kitchen_id})? It will become active again on the platform.</>
                )}
              </p>
              {blockKitchenError && (
                <p className="homecookt-modal-error">
                  <AlertTriangle size={14} />
                  {blockKitchenError}
                </p>
              )}
            </div>
            <div className="homecookt-modal-actions">
              <button
                className="homecookt-modal-btn homecookt-modal-btn--cancel"
                onClick={closeBlockKitchenModal}
                disabled={blockingKitchen}
              >
                Cancel
              </button>
              <button
                className={`homecookt-modal-btn ${blockKitchenAction === 'block' ? 'homecookt-modal-btn--danger' : 'homecookt-modal-btn--success'}`}
                onClick={handleBlockKitchen}
                disabled={blockingKitchen}
              >
                {blockingKitchen
                  ? (blockKitchenAction === 'block' ? 'Blocking...' : 'Unblocking...')
                  : (blockKitchenAction === 'block' ? 'Confirm Block' : 'Confirm Unblock')
                }
              </button>
            </div>
          </div>
        </div>
      )}
      {showKitchenDetailModal && selectedKitchen && (
        <div className="homecookt-modal-overlay" onClick={() => setShowKitchenDetailModal(false)}>
          <div className="homecookt-kitchen-detail-modal" onClick={(e) => e.stopPropagation()}>
            <button className="homecookt-modal-close" onClick={() => setShowKitchenDetailModal(false)}>
              <X size={18} />
            </button>
            <div className="homecookt-kitchen-detail-header">
              {selectedKitchen.kitchen_photo && selectedKitchen.kitchen_photo !== 'string' ? (
                <img src={selectedKitchen.kitchen_photo} alt={selectedKitchen.kitchen_name} className="homecookt-kitchen-detail-photo" />
              ) : (
                <div className="homecookt-kitchen-detail-photo-fallback">
                  <UtensilsCrossed size={32} />
                </div>
              )}
              <div className="homecookt-kitchen-detail-title">
                <h3>{selectedKitchen.kitchen_name}</h3>
                <p className="homecookt-kitchen-detail-subtitle">
                  ID #{selectedKitchen.kitchen_id} &middot; {sanitize(selectedKitchen.kitchen_type)} &middot; {sanitize(selectedKitchen.kitchen_category)}
                </p>
                <div className="homecookt-kitchen-detail-badges">
                  {!selectedKitchen.is_approved ? (
                    <span className="homecookt-kitchen-badge homecookt-kitchen-badge--pending">Pending Approval</span>
                  ) : selectedKitchen.is_blocked ? (
                    <span className="homecookt-kitchen-badge homecookt-kitchen-badge--inactive">Approved / Blocked</span>
                  ) : !selectedKitchen.is_online ? (
                    <span className="homecookt-kitchen-badge homecookt-kitchen-badge--pending">Approved / Offline</span>
                  ) : (
                    <span className="homecookt-kitchen-badge homecookt-kitchen-badge--active">Approved / Online</span>
                  )}
                  {selectedKitchen.is_accepting_orders && (
                    <span className="homecookt-kitchen-badge homecookt-kitchen-badge--accepting">Accepting Orders</span>
                  )}
                </div>
              </div>
            </div>

            <div className="homecookt-kitchen-detail-grid">
              <div className="homecookt-kitchen-detail-section">
                <h4><Users size={16} /> Owner & Contact</h4>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Owner</span>
                  <span>{sanitize(selectedKitchen.owner_name)}</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">User ID</span>
                  <span>{selectedKitchen.user_id ?? '—'}</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Contact</span>
                  <span>{sanitize(selectedKitchen.owner_phone || selectedKitchen.contact_number)}</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Email</span>
                  <span>{selectedKitchen.owner_email || selectedKitchen.created_by || '—'}</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Owner Status</span>
                  <span>
                    {selectedKitchen.owner_is_blocked
                      ? 'Blocked'
                      : selectedKitchen.owner_is_active
                        ? 'Active'
                        : 'Inactive'}
                  </span>
                </div>
                {selectedKitchen.owner_last_active && (
                  <div className="homecookt-kitchen-detail-row">
                    <span className="homecookt-kitchen-detail-label">Last Active</span>
                    <span>{formatCreatedAt(selectedKitchen.owner_last_active)}</span>
                  </div>
                )}
              </div>

              <div className="homecookt-kitchen-detail-section">
                <h4><MapPin size={16} /> Location</h4>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Address</span>
                  <span>{sanitize(selectedKitchen.kitchen_address)}</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">City</span>
                  <span>{sanitize(selectedKitchen.city)}</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">State</span>
                  <span>{sanitize(selectedKitchen.state)}</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Country</span>
                  <span>{sanitize(selectedKitchen.country)}</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Pincode</span>
                  <span>{selectedKitchen.pincode || '—'}</span>
                </div>
              </div>

              <div className="homecookt-kitchen-detail-section">
                <h4><Truck size={16} /> Delivery & Operations</h4>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Delivery Radius</span>
                  <span>{selectedKitchen.delivery_radius_km ?? '—'} km</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Delivery Fee</span>
                  <span>{selectedKitchen.delivery_fee != null ? `£${selectedKitchen.delivery_fee}` : '—'}</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Min. Order</span>
                  <span>{selectedKitchen.minimum_order_amount != null ? `£${selectedKitchen.minimum_order_amount}` : '—'}</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Avg. Prep Time</span>
                  <span>{selectedKitchen.average_preparation_time ?? '—'} min</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Opening Time</span>
                  <span>{selectedKitchen.opening_time || 'Not set'}</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Closing Time</span>
                  <span>{selectedKitchen.closing_time || 'Not set'}</span>
                </div>
              </div>

              <div className="homecookt-kitchen-detail-section">
                <h4><Star size={16} /> Performance & Earnings</h4>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Total Orders</span>
                  <span>{selectedKitchen.total_orders ?? 0}</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Total Earnings</span>
                  <span>{selectedKitchen.total_earnings != null ? `£${selectedKitchen.total_earnings}` : '—'}</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Avg. Rating</span>
                  <span>{selectedKitchen.average_rating ?? 0} / 5</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Total Reviews</span>
                  <span>{selectedKitchen.total_reviews ?? 0}</span>
                </div>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Created</span>
                  <span>{formatCreatedAt(selectedKitchen.created_at)}</span>
                </div>
                {selectedKitchen.rejection_reason && (
                  <div className="homecookt-kitchen-detail-row homecookt-kitchen-detail-row--warning">
                    <span className="homecookt-kitchen-detail-label">Rejection Reason</span>
                    <span>{selectedKitchen.rejection_reason}</span>
                  </div>
                )}
              </div>

              <div className="homecookt-kitchen-detail-section">
                <h4><FileCheck size={16} /> Document Verification</h4>
                <div className="homecookt-kitchen-detail-row">
                  <span className="homecookt-kitchen-detail-label">Status</span>
                  <span>
                    {selectedKitchen.document_verification_status === 'approved' ? (
                      <span className="homecookt-kitchen-badge homecookt-kitchen-badge--approved">Approved</span>
                    ) : selectedKitchen.document_verification_status === 'rejected' ? (
                      <span className="homecookt-kitchen-badge homecookt-kitchen-badge--rejected">Rejected</span>
                    ) : (
                      <span className="homecookt-kitchen-badge homecookt-kitchen-badge--pending">Pending</span>
                    )}
                  </span>
                </div>
                {(selectedKitchen.photo_id_url || selectedKitchen.training_certificate_url) && (
                  <div className="homecookt-kitchen-detail-row">
                    <span className="homecookt-kitchen-detail-label">Documents</span>
                    <span className="homecookt-doc-chips">
                      {selectedKitchen.photo_id_url && (
                        <a href={selectedKitchen.photo_id_url} target="_blank" rel="noopener noreferrer" className="homecookt-doc-chip homecookt-doc-chip--id" title={`View Photo ID (${sanitize(selectedKitchen.photo_id_type || 'document')})`}>
                          <FileText size={13} />
                          <span>Photo ID</span>
                          <ExternalLink size={11} />
                        </a>
                      )}
                      {selectedKitchen.training_certificate_url && (
                        <a href={selectedKitchen.training_certificate_url} target="_blank" rel="noopener noreferrer" className="homecookt-doc-chip homecookt-doc-chip--cert" title="View Training Certificate">
                          <Award size={13} />
                          <span>Certificate</span>
                          <ExternalLink size={11} />
                        </a>
                      )}
                    </span>
                  </div>
                )}
                {selectedKitchen.document_rejection_reason && (
                  <div className="homecookt-kitchen-detail-row homecookt-kitchen-detail-row--warning">
                    <span className="homecookt-kitchen-detail-label">Rejection Reason</span>
                    <span>{selectedKitchen.document_rejection_reason}</span>
                  </div>
                )}
                {selectedKitchen.document_verified_at && (
                  <div className="homecookt-kitchen-detail-row">
                    <span className="homecookt-kitchen-detail-label">Verified At</span>
                    <span>{formatCreatedAt(selectedKitchen.document_verified_at)}</span>
                  </div>
                )}
                {selectedKitchen.document_verified_by && (
                  <div className="homecookt-kitchen-detail-row">
                    <span className="homecookt-kitchen-detail-label">Verified By</span>
                    <span>{selectedKitchen.document_verified_by}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="homecookt-modal-actions">
              <button
                className="homecookt-modal-btn homecookt-modal-btn--cancel"
                onClick={() => setShowKitchenDetailModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
